import clsx from "clsx";
import { useField } from "formik";
import React from "react";
import Checkbox from "./Checkbox"; // Assuming Checkbox.jsx is in the same directory

interface CheckboxFieldProps {
  name: string;
  label?: string; // Optional label for the checkbox
  id?: string;
  disabled?: boolean;
  darkMode?: boolean;
  className?: string; // For additional styling of the wrapper for CheckboxField itself
  inverse?: boolean; // When true, unchecked = 1, checked = 0
}

const CheckboxField: React.FC<CheckboxFieldProps> = ({
  name,
  label,
  id,
  disabled,
  darkMode,
  className,
  inverse = false,
}) => {
  const [field, meta, helpers] = useField({ name, type: "checkbox" });

  // Handle inverse logic
  const handleChange = React.useCallback(() => {
    if (inverse) {
      // For inverse: toggle between 0 and 1
      const newValue = field.value === 0 ? 1 : 0;
      helpers.setValue(newValue);
    } else {
      // Normal checkbox behavior
      field.onChange({ target: { name: field.name, checked: !field.value } });
    }
    return null;
  }, [inverse, field.value, field.onChange, field.name, helpers]);

  const handleBlur = React.useCallback(() => {
    helpers.setTouched(true);
    return null;
  }, [helpers]);

  // Determine the visual checked state
  const isChecked = inverse ? field.value === 0 : !!field.value;

  return (
    <div className={clsx("flex flex-col", className)}>
      <div className="flex items-center">
        <Checkbox
          id={(id || name) as never}
          name={field.name as never}
          checked={isChecked}
          onChange={handleChange as never}
          onBlur={handleBlur as never}
          disabled={disabled}
          darkMode={darkMode}
        />
        {label && (
          <label
            htmlFor={id || name}
            className="text-grey-900 ml-2 font-body text-sm"
          >
            {label}
          </label>
        )}
      </div>
      {meta.touched && meta.error ? (
        <div className="mt-1 text-xs text-red-500">{meta.error}</div>
      ) : null}
    </div>
  );
};

export default CheckboxField;
